import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProFeature, UpgradeModal } from '../components/ProFeatures';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Plus, 
  Search, 
  Package,
  AlertTriangle,
  MoreVertical,
  Edit,
  Trash2,
  Crown
} from 'lucide-react';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InventoryPage() {
  const { isPro } = useAuth();
  const { showUpgrade, setShowUpgrade, UpgradeModalComponent } = useProFeature();
  const navigate = useNavigate();
  
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showLowStock, setShowLowStock] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    quantity: 0,
    min_stock: 5,
    unit_price: 0,
    category: ''
  });

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/inventory`, {
        credentials: 'include'
      });
      if (response.ok) {
        setItems(await response.json());
      } else if (response.status === 403) {
        setShowUpgrade(true);
      }
    } catch (error) {
      console.error('Error fetching inventory:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingItem 
        ? `${API_URL}/api/inventory/${editingItem.item_id}`
        : `${API_URL}/api/inventory`;
      
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          quantity: parseInt(formData.quantity),
          min_stock: parseInt(formData.min_stock),
          unit_price: parseFloat(formData.unit_price)
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al guardar');
      }

      toast.success(editingItem ? 'Artículo actualizado' : 'Artículo creado');
      setShowForm(false);
      setEditingItem(null);
      resetForm();
      fetchInventory();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async (itemId) => {
    if (!confirm('¿Estás seguro de eliminar este artículo?')) return;

    try {
      const response = await fetch(`${API_URL}/api/inventory/${itemId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Error al eliminar');

      toast.success('Artículo eliminado');
      fetchInventory();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description || '',
      quantity: item.quantity,
      min_stock: item.min_stock,
      unit_price: item.unit_price,
      category: item.category || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      quantity: 0,
      min_stock: 5,
      unit_price: 0,
      category: ''
    });
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(search.toLowerCase()) ||
      item.category?.toLowerCase().includes(search.toLowerCase());
    const matchesLowStock = !showLowStock || item.is_low_stock;
    return matchesSearch && matchesLowStock;
  });

  const lowStockCount = items.filter(i => i.is_low_stock).length;

  return (
    <div className="animate-fade-in" data-testid="inventory-page">
      <UpgradeModalComponent />
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            Inventario
            {!isPro && (
              <span className="text-sm font-normal text-slate-500">({items.length}/5 gratis)</span>
            )}
          </h1>
          <p className="text-slate-500">{items.length} artículo{items.length !== 1 ? 's' : ''}</p>
        </div>
        <Button
          onClick={() => setShowForm(true)}
          className="bg-[#1E3A5F] hover:bg-[#162e4d]"
          data-testid="add-inventory-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Artículo
        </Button>
      </div>

      {/* Low Stock Alert */}
      {lowStockCount > 0 && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <span className="text-amber-800">
              <strong>{lowStockCount}</strong> artículo{lowStockCount !== 1 ? 's' : ''} con stock bajo
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowLowStock(!showLowStock)}
            className={showLowStock ? 'bg-amber-100' : ''}
          >
            {showLowStock ? 'Ver todos' : 'Ver stock bajo'}
          </Button>
        </div>
      )}

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Buscar por nombre o categoría..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="search-inventory"
        />
      </div>

      {/* Items List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-20 w-full rounded-lg" />
          ))}
        </div>
      ) : filteredItems.length > 0 ? (
        <div className="space-y-3">
          {filteredItems.map((item) => (
            <div
              key={item.item_id}
              className={`card-base card-hover p-4 ${item.is_low_stock ? 'border-amber-200 bg-amber-50/50' : ''}`}
              data-testid={`inventory-item-${item.item_id}`}
            >
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  item.is_low_stock ? 'bg-amber-100' : 'bg-purple-100'
                }`}>
                  <Package className={`w-6 h-6 ${item.is_low_stock ? 'text-amber-600' : 'text-purple-600'}`} />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-slate-900">{item.name}</span>
                    {item.category && (
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">
                        {item.category}
                      </span>
                    )}
                    {item.is_low_stock && (
                      <span className="low-stock-alert">Stock bajo</span>
                    )}
                  </div>
                  {item.description && (
                    <p className="text-sm text-slate-500 truncate">{item.description}</p>
                  )}
                </div>

                <div className="text-right">
                  <p className={`text-2xl font-bold ${item.is_low_stock ? 'text-amber-600' : 'text-slate-900'}`}>
                    {item.quantity}
                  </p>
                  <p className="text-xs text-slate-500">
                    mín: {item.min_stock} • ${item.unit_price?.toLocaleString('es-MX')} c/u
                  </p>
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleEdit(item)}>
                      <Edit className="w-4 h-4 mr-2" />
                      Editar
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => handleDelete(item.item_id)}
                      className="text-red-600"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Eliminar
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <Package className="empty-state-icon" />
          <p className="text-slate-500 mb-4">
            {search || showLowStock ? 'No se encontraron artículos' : 'No hay artículos en inventario'}
          </p>
          {!search && !showLowStock && (
            <Button onClick={() => setShowForm(true)} className="bg-[#1E3A5F]">
              <Plus className="w-4 h-4 mr-2" />
              Agregar primer artículo
            </Button>
          )}
        </div>
      )}

      {/* Item Form Modal */}
      <Dialog open={showForm} onOpenChange={(open) => {
        if (!open) {
          setShowForm(false);
          setEditingItem(null);
          resetForm();
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Editar Artículo' : 'Nuevo Artículo'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Nombre *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="inventory-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="description">Descripción</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            
            <div>
              <Label htmlFor="category">Categoría</Label>
              <Input
                id="category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="Ej: Filtros, Aceites, Frenos"
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="quantity">Cantidad</Label>
                <Input
                  id="quantity"
                  type="number"
                  min="0"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  data-testid="inventory-quantity-input"
                />
              </div>
              <div>
                <Label htmlFor="min_stock">Stock mín.</Label>
                <Input
                  id="min_stock"
                  type="number"
                  min="0"
                  value={formData.min_stock}
                  onChange={(e) => setFormData({ ...formData, min_stock: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="unit_price">Precio ($)</Label>
                <Input
                  id="unit_price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.unit_price}
                  onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-[#1E3A5F]" data-testid="save-inventory-btn">
                {editingItem ? 'Guardar Cambios' : 'Crear Artículo'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
